import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { Geolocation } from '@capacitor/geolocation';
import { environment } from '../../../environments/environment';
import { CITIES } from '../../../environments/city.config';

/** A city resolved at runtime (from cities.json) — one isolated backend per city. */
export interface CityRuntime {
  id: string;
  name: string;
  center: [number, number];                 // [lat, lng]
  zoom: number;
  dataSource: 'gtfs' | 'lines';
  apiBaseUrl: string;                        // this city's silo API
  bbox: [number, number, number, number];   // [minLat, minLng, maxLat, maxLng]
}

const STORAGE_KEY = 'syncc.cityId';

/**
 * A fix this recent is returned instantly, so a relaunch resolves without
 * waiting on the GPS chip at all.
 */
const CACHED_FIX_MAX_AGE_MS = 5 * 60_000;

/** Cold-start budget for a first fix. Generous: a timeout here costs a picker. */
const GPS_TIMEOUT_MS = 12_000;

/**
 * How far from a city centre still counts as that city's region. Bboxes are
 * drawn tight around the mapped network, so a rider in an outer suburb
 * (Vinto, Sacaba, Viacha) can sit just outside one while plainly belonging to
 * that city. The supported cities are ~230 km apart, so a radius this size
 * cannot make two of them compete.
 */
const NEAR_CITY_RADIUS_KM = 80;

/**
 * Runtime city resolution for the single-binary native app:
 *   1) a previously chosen city (persisted), else
 *   2) geolocation → bbox match, else
 *   3) the first city in cities.json (fallback).
 * The active city's `apiBaseUrl` drives every API/socket call (see api.interceptor).
 * Adding a city later is a cities.json publish — no app-store release.
 */
@Injectable({ providedIn: 'root' })
export class CityService {
  private http = inject(HttpClient);

  private readonly _active = signal<CityRuntime>(this.buildTimeFallback());
  /** The active city; valid synchronously (defaults until resolve() completes). */
  readonly active = this._active.asReadonly();
  /** True when GPS failed and no saved city exists — app.component shows a picker. */
  readonly needsCityPicker = signal(false);
  /** True only when GPS position matched a supported city bbox — home page centers on user. */
  readonly gpsMatched = signal(false);

  private cities: CityRuntime[] = [];

  /** Base URL for the active city's silo API. */
  apiBaseUrl(): string {
    if (environment.apiUrl.startsWith('http://localhost')) return environment.apiUrl;
    return this._active().apiBaseUrl || environment.apiUrl;
  }

  /**
   * Resolve the active city at startup (called from an APP_INITIALIZER).
   * Geolocation is the source of truth and is re-evaluated every launch, so the
   * silo always follows where the user actually is: an exact bbox hit wins, and
   * failing that the nearest city within NEAR_CITY_RADIUS_KM. A saved choice is
   * only an offline / permission-denied fallback.
   *
   * The picker is the last resort — it should reach someone genuinely far from
   * every supported city, or someone whose location we could not read at all,
   * and nobody who is actually standing in one of these cities.
   */
  async resolve(): Promise<void> {
    await this.loadCities();

    try {
      const pos = await this.withTimeout(
        Geolocation.getCurrentPosition({
          enableHighAccuracy: false,
          maximumAge: CACHED_FIX_MAX_AGE_MS,
          timeout: GPS_TIMEOUT_MS,
        }),
        GPS_TIMEOUT_MS + 1000,
      );
      const { latitude, longitude } = pos.coords;
      const match = this.cities.find(c => this.inBbox(latitude, longitude, c.bbox))
        ?? this.nearestCityWithinRadius(latitude, longitude);
      if (match) { this._active.set(match); this.save(match.id); this.gpsMatched.set(true); return; }
      // GPS works but user is outside all supported cities.
      // Respect a previously saved manual choice; only prompt if none exists.
      const savedId = this.readSaved();
      const saved = savedId ? this.cities.find(c => c.id === savedId) : null;
      if (saved) { this._active.set(saved); return; }
      if (this.cities[0]) this._active.set(this.cities[0]);
      this.needsCityPicker.set(true);
      return;
    } catch { /* permission denied / timeout → fall back to last known */ }

    // Geolocation unavailable: use the last matched city if known.
    const savedId = this.readSaved();
    const saved = savedId ? this.cities.find(c => c.id === savedId) : null;
    if (saved) {
      this._active.set(saved);
      return;
    }
    // No saved city either — ask the user to pick.
    if (this.cities[0]) this._active.set(this.cities[0]);
    this.needsCityPicker.set(true);
  }

  /** Called from the city picker when the user makes a manual selection. */
  selectCity(city: CityRuntime): void {
    this.save(city.id);
    window.location.reload();
  }

  /** Returns the loaded city list for display in the picker. */
  availableCities(): CityRuntime[] { return this.cities; }

  // ── internals ───────────────────────────────────────────────────────────────

  private async loadCities(): Promise<void> {
    if (environment.apiUrl.startsWith('http://localhost')) {
      // Load bundled cities.json so the picker can show all cities.
      // apiBaseUrl() overrides to localhost regardless of the selection.
      try {
        const r = await firstValueFrom(this.http.get<CityRuntime[]>('assets/cities.json'));
        if (Array.isArray(r) && r.length) { this.cities = r; return; }
      } catch { /* fall through */ }
      this.cities = [this.buildTimeFallback()];
      return;
    }
    const remote = environment.production
      ? 'https://syncc.me/cities.json'
      : 'https://dev.syncc.me/cities.json';
    try {
      const r = await firstValueFrom(this.http.get<CityRuntime[]>(remote));
      if (Array.isArray(r) && r.length) { this.cities = r; return; }
    } catch { /* fall back to bundled */ }
    try {
      const r = await firstValueFrom(this.http.get<CityRuntime[]>('assets/cities.json'));
      if (Array.isArray(r) && r.length) { this.cities = r; return; }
    } catch { /* fall through */ }
    // Both the remote list and the bundled copy failed. An empty list would
    // render the picker with nothing to pick, so keep the build-time city.
    this.cities = [this.buildTimeFallback()];
  }

  private inBbox(lat: number, lng: number, b: [number, number, number, number]): boolean {
    return lat >= b[0] && lat <= b[2] && lng >= b[1] && lng <= b[3];
  }

  /** Closest city whose centre is within NEAR_CITY_RADIUS_KM, else null. */
  private nearestCityWithinRadius(lat: number, lng: number): CityRuntime | null {
    let nearest: CityRuntime | null = null;
    let nearestKm = Infinity;
    for (const c of this.cities) {
      const km = this.distanceKm(lat, lng, c.center[0], c.center[1]);
      if (km < nearestKm) { nearestKm = km; nearest = c; }
    }
    return nearestKm <= NEAR_CITY_RADIUS_KM ? nearest : null;
  }

  private distanceKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
    const toRad = (d: number) => (d * Math.PI) / 180;
    const dLat = toRad(bLat - aLat);
    const dLng = toRad(bLng - aLng);
    const h =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
    return 2 * 6371 * Math.asin(Math.sqrt(h));
  }

  private readSaved(): string | null {
    try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
  }
  private save(id: string): void {
    try { localStorage.setItem(STORAGE_KEY, id); } catch { /* ignore */ }
  }

  private withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
    return Promise.race([
      p,
      new Promise<T>((_, reject) => setTimeout(() => reject(new Error('timeout')), ms)),
    ]);
  }

  /** Last-resort default fallback. */
  private buildTimeFallback(): CityRuntime {
    const c = CITIES['cochabamba'];
    return {
      id: c.id, name: c.name, center: c.center, zoom: c.zoom, dataSource: c.dataSource,
      apiBaseUrl: environment.apiUrl, bbox: [-90, -180, 90, 180],
    };
  }
}
